import { Component, OnDestroy, inject } from '@angular/core';
import { Validators, NonNullableFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, NavigationCancel } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NotificationsService } from 's1m-angular-bootstrap-lib';

import { CurrentUserService } from '../services/current-user.service';
import { CurrentUser } from 'src/app/auth/models/account.model';
import { AccessEventsService } from '../services/access-events.service';
import { LoginBlocService } from './services/login-bloc.service';
import { LoginAction, LoginEvent } from './models/login-bloc.model';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { ButtonComponent } from '../../shared/button/button.component';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [LoginBlocService],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, ButtonComponent, TranslateModule, NgIf],
})
export class LoginComponent implements OnDestroy {
  fb = inject(NonNullableFormBuilder);
  router = inject(Router);
  route = inject(ActivatedRoute);
  notificationsService = inject(NotificationsService);
  translateService = inject(TranslateService);
  accessEvents = inject(AccessEventsService);
  currentUserService = inject(CurrentUserService);
  bloc = inject(LoginBlocService);
  localStorageService = inject(LocalStorageService);

  loading = false;
  returnUrl: string;
  routerEventsSubscription!: Subscription;
  form = this.fb.group({
    username: ['', Validators.required],
    password: ['', Validators.required],
  });
  showNewPassword = false;
  action: 'recovery' | 'login' = 'login';

  constructor() {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'];
    this.listenToActions();
    this.listenToBaseDataLoadError();
  }

  onLogin() {
    this.loading = true;
    this.notificationsService.clearAll();
    const { username, password } = this.form.getRawValue();
    this.bloc.addEvent(LoginEvent.fetchToken, { username, password });
  }

  listenToActions() {
    this.bloc.actions$.subscribe((action) => {
      switch (action.name) {
        case LoginAction.tokenFetched:
          this.localStorageService.token = action.payload.token;
          this.accessEvents.login();
          this.bloc.addEvent(LoginEvent.fetchUserInfo, {});
          break;
        case LoginAction.tokenFetchedFailed:
          this.manageLoadDataError(action.payload.httpError);
          break;
        case LoginAction.userInfoFetched:
          this.onValidLogin(action.payload.currentUser);
          break;
        case LoginAction.userInfoFetchedFailed:
          this.manageLoadDataError(action.payload.httpError);
          break;
        case LoginAction.loadBaseDataError:
          this.notificationsService.addError(this.translateService.instant('common.dataLoadError'));
          this.loading = false;
          break;
        case LoginAction.passwordRecoveredFailed:
          this.manageLoadDataError(action.payload.error);
          break;
        case LoginAction.passwordRecovered:
          this.onValidRecovery();
          break;
      }
    });
  }

  manageLoadDataError(error: HttpErrorResponse) {
    this.loading = false;
    this.form.get('password')?.reset();

    if (error.status === 400) {
      this.notificationsService.clearAll();
      const title = this.translateService.instant('login.wrongUserOrPass');
      const message = this.translateService.instant('login.invalidCredentials');
      this.notificationsService.addError(title, message);
      this.accessEvents.logout();
    }
  }

  onValidLogin(currentUser: CurrentUser) {
    this.currentUserService.currentUser = currentUser;
    const redirectUrl = this.returnUrl || '/';
    this.router.navigateByUrl(redirectUrl);
  }

  listenToBaseDataLoadError() {
    this.routerEventsSubscription = this.router.events
      .pipe(filter((routerEvent) => this.loading && routerEvent instanceof NavigationCancel))
      .subscribe(() => this.bloc.addEvent(LoginEvent.loadBaseDataError, {}));
  }

  onShowPassword() {
    this.showNewPassword = !this.showNewPassword;
  }

  onRecovery() {
    this.loading = true;
    const username = this.form.get('username')!.value;
    this.bloc.addEvent(LoginEvent.recoverPassword, { username });
  }

  onValidRecovery() {
    this.loading = false;
    this.notificationsService.addSuccess(this.translateService.instant('login.recoverPasswordMessage'), '', 5000);
    this.action = 'login';
    this.form.reset();
  }

  toggleAction() {
    this.action = this.action === 'login' ? 'recovery' : 'login';
  }

  ngOnDestroy() {
    this.routerEventsSubscription.unsubscribe();
  }
}
